import { useEffect } from 'react';
import { useNotificationStore } from '@/store/notificationStore';
import { useNamespaceSocket } from '@/contexts/SocketContext';
import toast from 'react-hot-toast';

export function useNotificationListeners() {
  const addNotification = useNotificationStore((state) => state.addNotification);
  const preferences = useNotificationStore((state) => state.preferences);

  // Socket connections
  const { socket: matchesSocket } = useNamespaceSocket('matches');
  const { socket: tournamentsSocket } = useNamespaceSocket('tournaments');
  const { socket: ladderSocket } = useNamespaceSocket('ladder');
  const { socket: gatherSocket } = useNamespaceSocket('gather');
  const { socket: friendsSocket } = useNamespaceSocket('friends');
  const { socket: chatSocket } = useNamespaceSocket('chat');
  const { socket: clientSocket } = useNamespaceSocket('client');

  // Helper to show toast notification
  const showToast = (title: string, message: string, type: 'success' | 'error' | 'info' = 'info') => {
    if (!preferences.enableToasts) return;

    const toastOptions = {
      icon: type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️',
      duration: 4000,
    };

    if (type === 'success') {
      toast.success(`${title}: ${message}`, toastOptions);
    } else if (type === 'error') {
      toast.error(`${title}: ${message}`, toastOptions);
    } else {
      toast(`${title}: ${message}`, toastOptions);
    }
  };

  // Matches namespace listeners
  useEffect(() => {
    if (!matchesSocket) return;

    matchesSocket.on('matchmaking:match_ready', (data) => {
      if (!preferences.categories.matches) return;

      addNotification({
        type: 'MATCH_READY',
        category: 'matches',
        priority: 'urgent',
        title: 'Match Ready!',
        message: 'Accept your match within 30 seconds',
        actionUrl: '/client/matchmaking',
        actionLabel: 'Accept Match',
        metadata: data,
        read: false,
      });

      showToast('Match Ready!', 'Accept your match now', 'success');
    });

    matchesSocket.on('matchmaking:match_found', (data) => {
      if (!preferences.categories.matches) return;

      addNotification({
        type: 'MATCH_FOUND',
        category: 'matches',
        priority: 'high',
        title: 'Match Found',
        message: `Match found for ${data.mode || 'game'}`,
        metadata: data,
        read: false,
      });
    });

    matchesSocket.on('match:completed', (data) => {
      if (!preferences.categories.matches) return;

      addNotification({
        type: 'MATCH_COMPLETED',
        category: 'matches',
        priority: 'normal',
        title: 'Match Completed',
        message: data.result || 'Your match has ended',
        actionUrl: `/matches/${data.matchId}`,
        actionLabel: 'View Results',
        metadata: data,
        read: false,
      });
    });

    matchesSocket.on('match:started', (data) => {
      if (!preferences.categories.matches) return;

      addNotification({
        type: 'MATCH_STARTED',
        category: 'matches',
        priority: 'high',
        title: 'Match Started',
        message: 'Your match is now live!',
        actionUrl: `/matches/${data.matchId}`,
        actionLabel: 'Join Server',
        metadata: data,
        read: false,
      });

      showToast('Match Started', 'Join the server now', 'success');
    });

    return () => {
      matchesSocket.off('matchmaking:match_ready');
      matchesSocket.off('matchmaking:match_found');
      matchesSocket.off('match:completed');
      matchesSocket.off('match:started');
    };
  }, [matchesSocket, addNotification, preferences]);

  // Tournaments namespace listeners
  useEffect(() => {
    if (!tournamentsSocket) return;

    tournamentsSocket.on('tournament:checked_in', (data) => {
      if (!preferences.categories.tournaments) return;

      addNotification({
        type: 'TOURNAMENT_CHECKIN_OPEN',
        category: 'tournaments',
        priority: 'high',
        title: 'Tournament Check-in',
        message: `Check-in successful for ${data.tournamentName || 'tournament'}`,
        actionUrl: `/tournaments/${data.tournamentId}`,
        metadata: data,
        read: false,
      });

      showToast('Tournament', 'Check-in successful', 'success');
    });

    tournamentsSocket.on('tournament:match_scheduled', (data) => {
      if (!preferences.categories.tournaments) return;

      addNotification({
        type: 'TOURNAMENT_MATCH_SCHEDULED',
        category: 'tournaments',
        priority: 'high',
        title: 'Match Scheduled',
        message: 'Your tournament match starts soon',
        actionUrl: `/tournaments/${data.tournamentId}`,
        actionLabel: 'View Match',
        metadata: data,
        read: false,
      });
    });

    tournamentsSocket.on('tournament:bracket_updated', (data) => {
      if (!preferences.categories.tournaments) return;

      addNotification({
        type: 'TOURNAMENT_BRACKET_UPDATED',
        category: 'tournaments',
        priority: 'normal',
        title: 'Bracket Updated',
        message: 'Tournament bracket has been updated',
        actionUrl: `/tournaments/${data.tournamentId}`,
        metadata: data,
        read: false,
      });
    });

    tournamentsSocket.on('tournament:started', (data) => {
      if (!preferences.categories.tournaments) return;

      addNotification({
        type: 'TOURNAMENT_STARTED',
        category: 'tournaments',
        priority: 'high',
        title: 'Tournament Started',
        message: `${data.tournamentName || 'Tournament'} has started!`,
        actionUrl: `/tournaments/${data.tournamentId}`,
        metadata: data,
        read: false,
      });

      showToast('Tournament Started', data.tournamentName || 'Tournament is live', 'success');
    });

    return () => {
      tournamentsSocket.off('tournament:checked_in');
      tournamentsSocket.off('tournament:match_scheduled');
      tournamentsSocket.off('tournament:bracket_updated');
      tournamentsSocket.off('tournament:started');
    };
  }, [tournamentsSocket, addNotification, preferences]);

  // Ladder namespace listeners
  useEffect(() => {
    if (!ladderSocket) return;

    ladderSocket.on('ladder:rank_changed', (data) => {
      if (!preferences.categories.ladder) return;

      const isRankUp = data.newRank < data.oldRank;
      addNotification({
        type: isRankUp ? 'LADDER_RANK_UP' : 'LADDER_RANK_DOWN',
        category: 'ladder',
        priority: 'high',
        title: isRankUp ? 'Rank Up!' : 'Rank Changed',
        message: `You are now rank #${data.newRank}`,
        actionUrl: '/ladder',
        metadata: data,
        read: false,
      });

      if (isRankUp) {
        showToast('Rank Up!', `You are now #${data.newRank}`, 'success');
      }
    });

    ladderSocket.on('ladder:challenge_received', (data) => {
      if (!preferences.categories.ladder) return;

      addNotification({
        type: 'LADDER_CHALLENGE_RECEIVED',
        category: 'ladder',
        priority: 'high',
        title: 'Challenge Received',
        message: `${data.challengerName || 'A player'} challenged you!`,
        actionUrl: '/ladder',
        actionLabel: 'View Challenge',
        metadata: data,
        read: false,
      });

      showToast('Challenge!', `${data.challengerName} challenged you`, 'info');
    });

    ladderSocket.on('ladder:season_ended', (data) => {
      if (!preferences.categories.ladder) return;

      addNotification({
        type: 'LADDER_SEASON_ENDED',
        category: 'ladder',
        priority: 'normal',
        title: 'Season Ended',
        message: `Season ${data.seasonNumber || ''} has ended. Final rank: #${data.finalRank}`,
        actionUrl: '/ladder',
        metadata: data,
        read: false,
      });
    });

    return () => {
      ladderSocket.off('ladder:rank_changed');
      ladderSocket.off('ladder:challenge_received');
      ladderSocket.off('ladder:season_ended');
    };
  }, [ladderSocket, addNotification, preferences]);

  // Gather namespace listeners
  useEffect(() => {
    if (!gatherSocket) return;

    gatherSocket.on('gather:ready_check', (data) => {
      if (!preferences.categories.gathers) return;

      addNotification({
        type: 'GATHER_READY_CHECK',
        category: 'gathers',
        priority: 'urgent',
        title: 'Ready Check',
        message: 'Click ready to start the gather',
        actionUrl: `/gather-lobby/${data.gatherId}`,
        actionLabel: 'Mark Ready',
        metadata: data,
        read: false,
      });

      showToast('Ready Check', 'Mark yourself as ready', 'info');
    });

    gatherSocket.on('gather:started', (data) => {
      if (!preferences.categories.gathers) return;

      addNotification({
        type: 'GATHER_STARTED',
        category: 'gathers',
        priority: 'high',
        title: 'Gather Started',
        message: 'Your gather is starting now!',
        actionUrl: `/gather-lobby/${data.gatherId}`,
        actionLabel: 'Join Server',
        metadata: data,
        read: false,
      });

      showToast('Gather Started', 'Join the server now', 'success');
    });

    gatherSocket.on('gather:player_joined', (data) => {
      if (!preferences.categories.gathers) return;

      addNotification({
        type: 'GATHER_PLAYER_JOINED',
        category: 'gathers',
        priority: 'normal',
        title: 'Player Joined',
        message: `${data.playerName || 'A player'} joined the gather`,
        actionUrl: `/gather-lobby/${data.gatherId}`,
        metadata: data,
        read: false,
      });
    });

    gatherSocket.on('gather:cancelled', (data) => {
      if (!preferences.categories.gathers) return;

      addNotification({
        type: 'GATHER_CANCELLED',
        category: 'gathers',
        priority: 'normal',
        title: 'Gather Cancelled',
        message: data.reason || 'The gather was cancelled',
        metadata: data,
        read: false,
      });
    });

    return () => {
      gatherSocket.off('gather:ready_check');
      gatherSocket.off('gather:started');
      gatherSocket.off('gather:player_joined');
      gatherSocket.off('gather:cancelled');
    };
  }, [gatherSocket, addNotification, preferences]);

  // Friends namespace listeners
  useEffect(() => {
    if (!friendsSocket) return;

    friendsSocket.on('friend:request_received', (data) => {
      if (!preferences.categories.friends) return;

      addNotification({
        type: 'FRIEND_REQUEST_RECEIVED',
        category: 'friends',
        priority: 'normal',
        title: 'Friend Request',
        message: `${data.fromUsername || 'Someone'} sent you a friend request`,
        actionUrl: '/friends',
        actionLabel: 'View Request',
        metadata: data,
        read: false,
      });

      showToast('Friend Request', `From ${data.fromUsername}`, 'info');
    });

    friendsSocket.on('friend:request_accepted', (data) => {
      if (!preferences.categories.friends) return;

      addNotification({
        type: 'FRIEND_REQUEST_ACCEPTED',
        category: 'friends',
        priority: 'normal',
        title: 'Friend Request Accepted',
        message: `${data.username || 'Your friend request'} accepted your friend request`,
        actionUrl: '/friends',
        metadata: data,
        read: false,
      });

      showToast('Friend Request', `${data.username} accepted`, 'success');
    });

    friendsSocket.on('friend:online', (data) => {
      if (!preferences.categories.friends) return;

      addNotification({
        type: 'FRIEND_ONLINE',
        category: 'friends',
        priority: 'low',
        title: 'Friend Online',
        message: `${data.username || 'A friend'} is now online`,
        actionUrl: '/friends',
        metadata: data,
        read: false,
      });
    });

    friendsSocket.on('party:invitation', (data) => {
      if (!preferences.categories.friends) return;

      addNotification({
        type: 'PARTY_INVITATION',
        category: 'friends',
        priority: 'high',
        title: 'Party Invitation',
        message: `${data.leader || 'Someone'} invited you to join their party`,
        actionUrl: '/friends',
        actionLabel: 'View Invitation',
        metadata: data,
        read: false,
      });

      showToast('Party Invitation', `From ${data.leader}`, 'info');
    });

    return () => {
      friendsSocket.off('friend:request_received');
      friendsSocket.off('friend:request_accepted');
      friendsSocket.off('friend:online');
      friendsSocket.off('party:invitation');
    };
  }, [friendsSocket, addNotification, preferences]);

  // Chat namespace listeners
  useEffect(() => {
    if (!chatSocket) return;

    chatSocket.on('private:new_message_notification', (data) => {
      if (!preferences.categories.messages) return;

      addNotification({
        type: 'PRIVATE_MESSAGE',
        category: 'messages',
        priority: 'normal',
        title: 'Private Message',
        message: `${data.username || 'Someone'}: ${data.preview || 'New message'}`,
        actionUrl: '/chat',
        actionLabel: 'View Message',
        metadata: data,
        read: false,
      });

      showToast('Message', `From ${data.username}`, 'info');
    });

    chatSocket.on('chat:mention', (data) => {
      if (!preferences.categories.messages) return;

      addNotification({
        type: 'CHAT_MENTION',
        category: 'messages',
        priority: 'high',
        title: 'Mentioned in Chat',
        message: `${data.username || 'Someone'} mentioned you`,
        actionUrl: '/chat',
        metadata: data,
        read: false,
      });

      showToast('Mention', `${data.username} mentioned you`, 'info');
    });

    return () => {
      chatSocket.off('private:new_message_notification');
      chatSocket.off('chat:mention');
    };
  }, [chatSocket, addNotification, preferences]);

  // Client namespace (system notifications)
  useEffect(() => {
    if (!clientSocket) return;

    // Active Match Notification
    clientSocket.on('match:active_match', (data) => {
      if (!preferences.categories.matches) return;

      addNotification({
        type: 'ACTIVE_MATCH',
        category: 'matches',
        priority: 'urgent',
        title: 'Active Match Found!',
        message: `You have an active ${data.mode || 'match'} - Click to rejoin`,
        actionUrl: '/client/matchmaking',
        actionLabel: 'Rejoin Match',
        metadata: data,
        read: false,
      });

      showToast('Active Match', 'Click notification to rejoin', 'info');
    });

    clientSocket.on('system:announcement', (data) => {
      if (!preferences.categories.system) return;

      addNotification({
        type: 'SYSTEM_ANNOUNCEMENT',
        category: 'system',
        priority: data.priority || 'normal',
        title: data.title || 'System Announcement',
        message: data.message,
        metadata: data,
        read: false,
      });

      if (data.priority === 'urgent' || data.priority === 'high') {
        showToast('Important', data.message, 'info');
      }
    });

    clientSocket.on('system:maintenance', (data) => {
      if (!preferences.categories.system) return;

      addNotification({
        type: 'SYSTEM_MAINTENANCE',
        category: 'system',
        priority: 'high',
        title: 'Scheduled Maintenance',
        message: data.message || 'System maintenance scheduled',
        metadata: data,
        read: false,
      });
    });

    clientSocket.on('system:update', (data) => {
      if (!preferences.categories.system) return;

      addNotification({
        type: 'SYSTEM_UPDATE',
        category: 'system',
        priority: 'normal',
        title: 'System Update',
        message: data.message || 'New features available',
        metadata: data,
        read: false,
      });
    });

    return () => {
      clientSocket.off('match:active_match');
      clientSocket.off('system:announcement');
      clientSocket.off('system:maintenance');
      clientSocket.off('system:update');
    };
  }, [clientSocket, addNotification, preferences]);
}
